// The Dock at the top of a phone's screen: what the header row is elsewhere
// (the person, 2026-10-18: "a Dock at the top, with the favorite and the
// dots menu; the title lives in the body").
//
// Promise:
// - It floats: the screen's body scrolls under its glass (phone.css gives the
//   body's scroller the room to start below it, `--top-dock-clearance`).
// - Back in history is a round glass button at the left; it is not there when
//   there is nothing before (no faded button floating alone).
// - The screen's buttons sit on one glass surface at the right, each a cell
//   of TOP_DOCK_CELL by TOP_DOCK_HEIGHT, icons at 18: phone.css reshapes
//   whatever button a screen passes (its icon buttons, menu buttons, the
//   star), so the screens keep one set of actions for every width.
// - Smaller than the Dock at the bottom and nearer the sides (the person,
//   2026-10-18: "the top Dock can be smaller, and less room at the sides").
// - With nothing on it (a first screen without back, a list whose "+" is on
//   the bottom Dock) it takes no room: the title or the body starts at the top.
// - A list's title is drawn under it and goes up with the list's search band
//   and rows as the list is scrolled (list-search.tsx): it took the top of
//   the screen for good (the person, 2026-10-18).
// - A detail has no title here: its body has its own.
import { useRouter } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import type { MouseEvent } from "react";
import type { PhoneTopDockProps } from "../shell/phone";
import { GLASS } from "../ui/glass";
import { Icon } from "../ui/icon";
import { DockSurface } from "./dock-row";
import { usePageBack } from "./history";

/** From the top of the panel to the Dock (the 12 px strip above it is solid in a home screen app). */
export const TOP_DOCK_TOP = 12;
/** The Dock's height and its round button's. */
export const TOP_DOCK_HEIGHT = 40;
/** One button's width on it (a little wider than high, as the Dock's). */
export const TOP_DOCK_CELL = 44;
/** From the screen's sides. */
export const TOP_DOCK_SIDE = 20;
/** The room it takes from the top: above it, it, 8 under (phone.css `--top-dock-clearance`). */
export const TOP_DOCK_CLEARANCE = TOP_DOCK_TOP + TOP_DOCK_HEIGHT + 8;

/**
 * The status bar's room above the Dock (phone.css `--phone-top`): zero unless
 * the page runs under the status bar (an app's web view over the screen).
 */
export function phoneTopInset(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;padding-top:env(safe-area-inset-top, 0px)";
  document.body.append(probe);
  const inset = Number.parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return inset;
}

const keepFocus = (e: MouseEvent) => e.preventDefault();

function Back() {
  const router = useRouter();
  // A menu's or a drawer's own history entry is not a page (history.ts).
  const { can, back } = usePageBack(router.history);
  if (!can) return <span />;
  return (
    <button
      type="button"
      aria-label={m.shell_back()}
      onMouseDown={keepFocus}
      onClick={back}
      className={`${GLASS} glass-icon pointer-events-auto flex flex-none cursor-pointer items-center justify-center text-fg`}
      // A circle: alone, the cells' 44 by 40 read as a wide pill (the person,
      // 2026-10-18). The cells stay wider on their surface.
      style={{
        width: TOP_DOCK_HEIGHT,
        height: TOP_DOCK_HEIGHT,
        borderRadius: TOP_DOCK_HEIGHT / 2,
      }}
    >
      <Icon kind="back" size={18} />
    </button>
  );
}

export function TopDock({ back, actions, title, hostBack }: PhoneTopDockProps) {
  const hasActions = actions != null && actions !== false;
  const titled = title != null && title !== "";
  // A list's way back is drawn twice, in the floating Dock and in the
  // title's row; phone.css shows the one in the title's row while the Dock
  // has no button of the screen's (the screen's actions may render nothing
  // on a phone). There it goes up with the title (list-search.tsx):
  // floating, it stood over the title and the search field while they went
  // up under it, and the Dock's room above the title read as a blank band
  // under the status bar (the person, 2026-10-18; the old web's list had one
  // header row).
  const inline = titled && back;
  return (
    <>
      {/* No height of its own: it floats over what follows. Over the body
          (`float`: phone.css gives the body's scroller its room) or over a
          list's title (`titled`: the title has the room). Above the top
          edge's solid strip (z 29), which cut its glass's rim and shadow. */}
      <div
        data-top-dock={titled ? "titled" : "float"}
        data-host-back={hostBack ? "" : undefined}
        className="relative z-30 h-0 flex-none"
      >
        <div
          className="pointer-events-none absolute flex items-center justify-between gap-[7px]"
          style={{
            // Below the status bar when the page runs under it (phone.css `--phone-top`).
            top: `calc(${TOP_DOCK_TOP}px + var(--phone-top, 0px))`,
            left: TOP_DOCK_SIDE,
            right: TOP_DOCK_SIDE,
          }}
        >
          {back ? (
            <span data-floating-back="" className="flex">
              <Back />
            </span>
          ) : (
            <span />
          )}
          {hasActions && (
            <DockSurface
              data-top-dock-actions=""
              className="min-w-0 px-0.5"
              style={{ borderRadius: TOP_DOCK_HEIGHT / 2 }}
            >
              {actions}
            </DockSurface>
          )}
        </div>
      </div>
      {titled && (
        // A list's title under the Dock. The list's search band takes it up
        // with itself (list-search.tsx): opaque and over the list's box, which
        // reaches up under it.
        <div
          data-top-dock-title=""
          className="relative z-[1] flex-none bg-surface"
        >
          {inline && (
            <span data-inline-back="" className="hidden">
              <Back />
            </span>
          )}
          <h1 className="min-w-0 truncate px-4 pb-2 text-[21px] leading-[1.35] font-normal tracking-[-0.01em] text-fg">
            {title}
          </h1>
        </div>
      )}
    </>
  );
}
